import React from 'react';
import PropTypes from 'prop-types';

// @atlaskit
import { DatePicker, TimePicker } from '@atlaskit/datetime-picker';
import { FieldTextStateless } from '@atlaskit/field-text';
import Button from '@atlaskit/button';
import Calendar from '@atlaskit/calendar';
import CalendarFilledIcon from '@atlaskit/icon/glyph/calendar-filled';
import CrossCircleIcon from '@atlaskit/icon/glyph/cross-circle';
import EditorDateIcon from '@atlaskit/icon/glyph/editor/date';
import EditorRecentIcon from '@atlaskit/icon/glyph/editor/recent';
import Modal from '@atlaskit/modal-dialog';
import RecentIcon from '@atlaskit/icon/glyph/recent';
import Select from '@atlaskit/select';
import Toggle from '@atlaskit/toggle';

import Styled from './styled';
import Helper from './helpers';
import ConditionToggle from './condition-toggle';

const bool = ['bit'];
const int = ['int', 'numeric'];
const time = ['datetime'];
const operators = ['=', '>', '<', '>=', '<=', '<>'];

class Condition extends React.Component {
  constructor(props) {
    super(props);
    this.console = this.props.console;
    this.data = {
      uuid: this.props.uuid,
      condition: this.props.condition,
      field: this.props.field,
      type: this.props.type,
      operator: this.props.operator,
      value: this.props.value,
    };
    this.state = {
      modal: '',
      update: new Date().getTime(),
    };

    this.onChangeConditionToggle = this.onChangeConditionToggle.bind(this);
    this.onChangeFieldSelect = this.onChangeFieldSelect.bind(this);
  }

  componentWillMount() {
    this.console.log('© Condition - Prepared');
    if (!this.data.value) {
      this.data.value = this.getFieldTextDefaultValue();
    }
    this.synchronize();
  }

  shouldComponentUpdate(nextProps, nextState) {
    if (Helper.comparison(this.data, nextProps)) {
      return true;
    } if (Helper.comparison(this.props, nextProps)) {
      return true;
    } if (Helper.comparison(this.state, nextState)) {
      return true;
    }
    return false;
  }

  componentDidUpdate() {
    this.console.log('© Condition - Rendered');
    this.synchronize();
  }

  onChangeConditionToggle() {
    this.console.log('© Condition - Condition Changed');
    this.data.condition = this.data.condition === 'and' ? 'or' : 'and';
    this.synchronize();
  }

  onChangeFieldSelect(option) {
    this.console.log('© Condition - Field Select Changed');
    this.data.field = option.label;
    this.data.type = option.type;
    this.data.value = this.getFieldTextDefaultValue();
    this.update();
  }

  getFieldTextDefaultValue() {
    if (time.includes(this.data.type)) {
      return Helper.getToday();
    } if (int.includes(this.data.type)) {
      return 1;
    } if (bool.includes(this.data.type)) {
      return false;
    }
    return '';
  }

  getFieldTextInput() {
    if (time.includes(this.data.type)) {
      return this.renderTypeByTimeInput();
    } if (int.includes(this.data.type)) {
      return this.renderTypeByIntInput();
    } if (bool.includes(this.data.type)) {
      return this.renderTypeByBoolean();
    }
    return this.renderTypeByStringInput();
  }

  update() {
    this.setState({ update: new Date().getTime() });
  }

  synchronize() {
    this.props.synchronize(this.props.uuid, this.data);
  }

  updateDateTime(type, value) {
    const [date, timezone] = this.data.value.split(' ');
    const [hour, minute, second] = timezone.split(':');

    if (type === 'date') {
      this.data.value = `${value} ${timezone}`;
    }

    if (type === 'hour') {
      this.data.value = `${date} ${value}:${minute}:${second}`;
    }

    if (type === 'minute') {
      this.data.value = `${date} ${hour}:${value}:${second}`;
    }

    if (type === 'second') {
      this.data.value = `${date} ${hour}:${minute}:${value}`;
    }

    this.update();
  }

  renderTypeByBoolean() {
    const props = {
      isDefaultChecked: this.data.value,
      size: 'large',
      onChange: () => {
        this.console.log('© Condition - Value Changed');
        this.data.value = !this.data.value;
        this.synchronize();
      },
    };

    return (
      <div>
        <Styled.OparatorLabel>FALSE</Styled.OparatorLabel>
        <span style={{ verticalAlign: 'middle', display: 'inline-block' }}>
          <Toggle {...props} />
        </span>
        <Styled.OparatorLabel>TRUE</Styled.OparatorLabel>
      </div>
    );
  }

  renderTypeByIntInput() {
    const props = {
      compact: true,
      max: '99',
      min: '1',
      shouldFitContainer: true,
      type: 'number',
      value: this.data.value,
      onChange: (event) => {
        this.console.log('© Condition - Value Changed');
        this.data.value = event.target.value;
        this.update();
      },
    };

    return <FieldTextStateless {...props} />;
  }

  renderTypeByMobileTimeInput() {
    const { modal } = this.state;
    const [date, timezone] = this.data.value.split(' ');
    const [day, month, year] = date.split('.');
    const [hour, minute, second] = timezone.split(':');

    const dataPickerStyle = {
      paddingBottom: 0,
      paddingLeft: 0,
      paddingTop: 0,
    };

    const timePickerStyle = {
      paddingBottom: 0,
      paddingRight: 0,
      paddingTop: 0,
    };

    const modalDialog = {
      width: 318,
      scrollBehavior: 'outside',
      onClose: () => this.setState({ modal: '' }),
    };

    const datePickerButton = {
      iconAfter: <CalendarFilledIcon size="small" />,
      onClick: () => this.setState({ modal: 'datePicker' }),
      shouldFitContainer: true,
    };

    const datePicker = {
      dateFormat: 'DD.MM.YYYY',
      selected: `${year}-${month}-${day}`,
      onSelect: value => this.updateDateTime('date', `${value.day}.${value.month}.${value.year}`),
    };

    const timePickerButton = {
      iconAfter: <RecentIcon size="small" />,
      onClick: () => this.setState({ modal: 'timePicker' }),
      shouldFitContainer: true,
    };

    const timePickerInput = {
      compact: true,
      maxLength: 2,
      min: 0,
      shouldFitContainer: true,
      type: 'number',
    };

    return (
      <Styled.Flex>
        <Styled.FlexItem style={dataPickerStyle}>
          <Button {...datePickerButton}>{date}</Button>
          {modal === 'datePicker' && (
            <Modal {...modalDialog}>
              <div style={{ color: '#505F79', margin: '0 -20px' }}>
                <Calendar {...datePicker} />
              </div>
            </Modal>
          )}
        </Styled.FlexItem>
        <Styled.FlexItem style={timePickerStyle} nowrap>
          <div style={{ width: 110 }}>
            <Button {...timePickerButton}>{timezone}</Button>
            {modal === 'timePicker' && (
              <Modal {...modalDialog}>
                <div style={{ color: '#505F79', paddingBottom: '20px' }}>
                  <Styled.Flex>
                    <Styled.FlexItem>
                      <FieldTextStateless
                        {...timePickerInput}
                        label="Hour"
                        max={24}
                        value={hour}
                        onChange={({ target: { value } }) => this.updateDateTime('hour', value)}
                      />
                    </Styled.FlexItem>
                    <Styled.FlexItem>
                      <FieldTextStateless
                        {...timePickerInput}
                        label="Minute"
                        max={59}
                        value={minute}
                        onChange={({ target: { value } }) => this.updateDateTime('minute', value)}
                      />
                    </Styled.FlexItem>
                    <Styled.FlexItem>
                      <FieldTextStateless
                        {...timePickerInput}
                        label="Second"
                        max={59}
                        value={second}
                        onChange={({ target: { value } }) => this.updateDateTime('second', value)}
                      />
                    </Styled.FlexItem>
                  </Styled.Flex>
                </div>
              </Modal>
            )}
          </div>
        </Styled.FlexItem>
      </Styled.Flex>
    );
  }

  renderTypeByTimeInput() {
    if (this.props.isMobile) return this.renderTypeByMobileTimeInput();
    const date = new Date(Helper.reverseDayMonth(this.data.value).replace(/\./g, '/'));
    this.props.console.log('value', Helper.getTime(date));

    const props = {
      date: {
        dateFormat: 'DD.MM.YYYY',
        defaultValue: date.toISOString(),
        icon: EditorDateIcon,
        placeholder: `e.g. ${Helper.getDate(date)}`,
        spacing: 'compact',
        onChange: (value) => {
          if (value !== 'Invalid Date') {
            const array = value.split('-').reverse();
            const split = this.data.value.split(' ');
            this.data.value = `${array.join('.')} ${split[1]}`;
            this.synchronize();
          }
        },
      },
      time: {
        defaultValue: Helper.getTime(date),
        icon: EditorRecentIcon,
        onChange: (value) => {
          if (value !== 'Invalid Date') {
            const split = this.data.value.split(' ');
            // Remove AM/PM from component value
            const cleanTime = value.replace(/[ap]m/gi, '');
            this.data.value = `${split[0]} ${cleanTime}:00`;
            this.synchronize();
          }
        },
        spacing: 'compact',
        timeFormat: 'HH:mm',
        timeIsEditable: true,
        times: Helper.get24Hours(),
      },
    };

    return (
      <Styled.Flex>
        <Styled.FlexItem style={{ paddingTop: 0, paddingBottom: 0, paddingLeft: 0 }}>
          <div style={{ color: '#505F79' }}>
            <DatePicker {...props.date} />
          </div>
        </Styled.FlexItem>
        <Styled.FlexItem style={{ paddingTop: 0, paddingBottom: 0, paddingRight: 0 }} nowrap>
          <div style={{ color: '#505F79', width: 100 }}>
            <TimePicker {...props.time} />
          </div>
        </Styled.FlexItem>
      </Styled.Flex>
    );
  }

  renderTypeByStringInput() {
    const props = {
      compact: true,
      shouldFitContainer: true,
      value: this.data.value,
      onChange: (event) => {
        this.data.value = event.target.value;
        this.update();
      },
    };

    return <FieldTextStateless {...props} />;
  }

  renderFieldSelect() {
    const items = this.props.fields.map(elem => ({
      label: elem.name,
      type: elem.type,
      value: elem.name,
    }));

    const props = {
      defaultValue: items.find(elem => elem.label === this.data.field),
      isSearchable: !this.props.isMobile,
      onChange: this.onChangeFieldSelect,
      options: items,
      placeholder: 'Field',
      spacing: 'compact',
      styles: {
        menu: style => ({
          ...style,
          minWidth: 180,
        }),
      },
    };

    return <Select {...props} />;
  }

  renderOperatorSelect() {
    const items = operators.map(elem => ({
      label: elem,
      value: elem,
    }));

    const props = {
      defaultValue: items.find(elem => elem.label === this.data.operator),
      options: items,
      spacing: 'compact',
      isSearchable: !this.props.isMobile,
      onChange: (option) => {
        this.data.operator = option.label;
        this.synchronize();
      },
    };

    return <Select {...props} />;
  }

  renderRemoveButton() {
    const props = {
      onClick: () => this.props.remove(this.props.uuid),
      iconAfter: <CrossCircleIcon size="small" label="Remove Condition" />,
    };

    return this.props.isMobile
      ? <Button {...props} />
      : <Button {...props}>Remove</Button>;
  }

  render() {
    const { isMobile } = this.props;

    return (
      <Styled.Condition data-update={this.state.update}>
        <Styled.FlexItem nospace nowrap>
          <Styled.Flex>
            <Styled.FlexItem nowrap>
              <ConditionToggle
                disabled={this.props.index}
                isMobile={isMobile}
                onChange={this.onChangeConditionToggle}
                value={this.data.condition}
              />
            </Styled.FlexItem>
            <Styled.FlexItem style={{ minWidth: isMobile ? 85 : 180 }}>
              {this.renderFieldSelect()}
            </Styled.FlexItem>
            {!bool.includes(this.data.type) ? (
              <Styled.FlexItem nowrap>
                <div style={{ width: 55 }}>
                  {this.renderOperatorSelect()}
                </div>
              </Styled.FlexItem>
            ) : null}
          </Styled.Flex>
        </Styled.FlexItem>
        <Styled.FlexItem nospace>
          <Styled.Flex>
            <Styled.FlexItem>
              <Styled.FieldText>
                {this.getFieldTextInput()}
              </Styled.FieldText>
            </Styled.FlexItem>
            <Styled.FlexItem nowrap>
              {this.renderRemoveButton()}
            </Styled.FlexItem>
          </Styled.Flex>
        </Styled.FlexItem>
      </Styled.Condition>
    );
  }
}

Condition.propTypes = {
  condition: PropTypes.string,
  console: PropTypes.shape().isRequired,
  field: PropTypes.string,
  fields: PropTypes.arrayOf(PropTypes.object).isRequired,
  index: PropTypes.number.isRequired,
  isMobile: PropTypes.bool.isRequired,
  operator: PropTypes.string,
  remove: PropTypes.func.isRequired,
  synchronize: PropTypes.func.isRequired,
  type: PropTypes.string,
  uuid: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
  ]),
};

Condition.defaultProps = {
  condition: 'and',
  field: '',
  operator: operators[0],
  type: '',
  value: '',
};

export default Condition;
